import React, { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { INSTANT_INSTRUMENTAL } from '@disco/constants/FeatureTypes'
import {
  createInstantACappella,
  createInstantInstrumental,
} from '@disco/state-management/actions/ExplorerActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { isAudioSeparationTermsAccepted } from '@disco/state-management/selectors/user'

import type { AudioSeparationDialogState } from 'types/audio-separation'
import type { TrackId } from 'types/track'

import { useDispatch, useSelector } from 'hooks/use-redux'

const InstantInstrumental = (): null => {
  const location = useLocation()
  const state = location.state as AudioSeparationDialogState | null
  const hasDialogState = state !== null
  const params = useParams<{ trackId: string }>()
  const trackIdStr = params.trackId!
  const trackId = parseInt(trackIdStr, 10) as TrackId
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isFeatureEnabled: boolean = useFeatureEnabled(INSTANT_INSTRUMENTAL)
  const audioSeparationTermsAccepted: boolean = useSelector(
    isAudioSeparationTermsAccepted,
  )
  const separationType = state?.separationType ?? 'instrumental'
  const isAutoTagged = state?.autoTagged === true

  useEffect(() => {
    if (!hasDialogState) {
      navigate('/', { replace: true })
      return
    }

    if (!isFeatureEnabled) {
      navigate(state.returnTo ?? '/', { replace: true })
    }
  }, [hasDialogState, isFeatureEnabled, navigate, state])

  useEffect(() => {
    if (!hasDialogState || !isFeatureEnabled) return

    if (!audioSeparationTermsAccepted) {
      navigate(`/explorer/audio-separation-terms-dialog`, {
        replace: true,
        state: {
          returnTo: location.pathname,
          originalReturnTo: state?.returnTo ?? '/',
          separationType,
          autoTagged: isAutoTagged,
        } satisfies AudioSeparationDialogState,
      })
      return
    }

    // Terms are accepted - dispatch the action and return to the original page
    // without showing any quota check UI (quota check removed per STL-592).
    dispatch(
      separationType === 'instrumental'
        ? createInstantInstrumental(trackId)
        : createInstantACappella(trackId),
    )
    navigate(state?.returnTo ?? '/', { replace: true })
  }, [
    audioSeparationTermsAccepted,
    dispatch,
    hasDialogState,
    isAutoTagged,
    isFeatureEnabled,
    location.pathname,
    navigate,
    separationType,
    state?.returnTo,
    trackId,
  ])

  return null
}

export default InstantInstrumental
